import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import Benefits from './Benefits';
import Features from './Features';
import CallToAction from './CallToAction';

const FreeTrial = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [showMonthly, setShowMonthly] = React.useState(false);

  return (
    <>
      <Helmet>
        <title>Free Trial - Sales Intercept</title>
        <meta name="description" content="Start your free trial of Sales Intercept. Add engaging videos to your website in minutes to boost conversions and engagement. No credit card required. Try it risk-free!" />
      </Helmet>
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-40 lg:pt-48 pb-12 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
              Like what you see? <br />
              <span className="text-purple-si">Try it on your site for free!</span>
            </h1>
            <p className="text-xl text-gray-700 mb-8">
              Add engaging videos to your website in minutes. Boost conversions and engagement with our easy-to-install widget.
              <span className="block font-bold mt-2">No Credit Card Required!</span>
            </p>
            <div className="flex justify-center">
              <a href="https://dashboard.salesintercept.com/signup" 
                 className="btn btn-primary">
                Start Free Trial
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto">
            {/* Promotional Banner */}
            <div className="bg-gradient-to-r from-purple-si to-orange-dark-si text-white p-4 rounded-lg text-center mb-8">
              <p className="font-semibold mb-2">
                🔥 Limited Time Pricing — Lock in these rates before they increase! 🔥
              </p>
              <p className="text-sm">
                Kickstart your trial — on us!<br />
                We'll set everything up on your site and even load your first 3 videos. <span className="font-bold">$250 value, totally free.</span>
              </p>
              <p className="text-sm mt-2 font-bold">
                No Credit Card Required to Start Your Free Trial!
              </p>
            </div>

            {/* Pricing Toggle */}
            <div className="flex justify-center items-center gap-4 mb-8">
              <span className={`font-semibold ${!showMonthly ? 'text-purple-si' : 'text-gray-500'}`}>Annual</span>
              <button 
                onClick={() => setShowMonthly(!showMonthly)}
                className={`relative w-14 h-7 rounded-full transition-colors duration-300 focus:outline-none ${showMonthly ? 'bg-purple-si' : 'bg-gray-300'}`}
              >
                <div className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-transform duration-300 ${showMonthly ? 'translate-x-8' : 'translate-x-1'}`}></div>
              </button>
              <span className={`font-semibold ${showMonthly ? 'text-purple-si' : 'text-gray-500'}`}>Monthly</span>
            </div>

            <div className="bg-gradient-to-r from-purple-si to-purple-light-si rounded-2xl overflow-hidden shadow-xl">
              <div className="p-8 text-white">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-3xl font-bold">Everything You Need</h2>
                    <p className="opacity-90">One simple plan, all features included</p>
                  </div>
                  <div className="bg-yellow-si/20 text-white font-bold py-1 px-3 rounded-full text-sm">
                    Limited Time
                  </div>
                </div>

                <div className="flex items-center mb-8">
                  {showMonthly ? (
                    <>
                      <div className="text-4xl font-bold">$67</div>
                      <div className="ml-2 opacity-90">/month</div>
                    </>
                  ) : (
                    <>
                      <div className="text-4xl font-bold">$490</div>
                      <div className="ml-2 opacity-90">/year</div>
                      <div className="ml-2 text-sm opacity-90">(4 and a half months free!)</div>
                    </>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>3 Video Uploads ($250 setup included)</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Unlimited Views</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Advanced Targeting Options</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Performance Analytics</span>
                  </div>
                </div>

                <div className="mt-8">
                  <a 
                    href="https://dashboard.salesintercept.com/signup"
                    className="block w-full bg-white text-purple-si text-center py-3 rounded-lg font-semibold hover:bg-white/90 transition-colors"
                  >
                    Start 14-Day Free Trial
                  </a>
                  <p className="text-center mt-2 text-sm font-medium">No Credit Card Required!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Benefits />
      <Features />
      <CallToAction />
    </div>
    </>
  );
};

export default FreeTrial;